import {TagSelection} from '../../tags/tag-selection';
import {SearchStateService} from '../../search/search-state.service';
import {SearchOverlayService} from '../../search/search-overlay.service';
import {ChangeDetectorRef, Component, DestroyRef, ElementRef, HostBinding, HostListener, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild, inject} from '@angular/core';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {App} from '@capacitor/app';
import {CodeScan} from '../../shared/scanning/code-scan';
import {SearchScheduler} from '../../shared/search/search-scheduler';
import {Capacitor} from '@capacitor/core';
import {AnalyticsService} from '../../shared/analytics/analytics.service';
import {MenuController, type RefresherCustomEvent, type ScrollCustomEvent} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {combineLatest, filter, firstValueFrom, tap} from 'rxjs';
import {Store} from '@ngrx/store';
import type {PluginListenerHandle} from '@capacitor/core';
import {environment} from '../../../environments/environment';
import {AuthService} from '../../auth/auth.service';
import {ApiCompatibilityService} from '../../shared/api/api-compatibility.service';
import type {AuthSession, CurrentUserResponse} from '../../auth/auth.models';
import {
  DEFAULT_GLOBAL_SEARCH_VISIBILITY_MODE,
  GlobalSearchContainerResult,
  GlobalSearchItemResult,
  GlobalSearchResults,
  GlobalSearchService,
  type GlobalSearchVisibilityMode,
} from '../../shared/search/global-search.service';
import {AppLanguageService} from '../../shared/i18n/app-language.service';
import {buildPlatformAwareRoutePathname, stripLocalizedRouteLanguageFromPathname} from '../../shared/i18n/app-language';
import {
  PRODUCT_CONTAINER_MENU_ARIA_LABEL_KEY,
  PRODUCT_CONTAINER_MENU_ITEMS,
} from '../../shared/context-menu/product-context-menu';
import {
  CONTAINER_ITEM_MENU_ARIA_LABEL_KEY,
  CONTAINER_ITEM_MENU_ITEMS,
} from '../../containers/item-context-menu';
import {handleContainerMenuItemSelect as handleContainerActionMenuItemSelect} from '../../containers/container-action-menu';
import {handleItemMenuItemSelect as handleItemActionMenuItemSelect} from '../../items/item-action-menu';
import type {SmContextMenuItemModel} from '../../shared/components/sm-context-menu-item/sm-context-menu-item.component';
import {SmConfirmationDialogService} from '../../shared/components/sm-confirmation-dialog/sm-confirmation-dialog.service';
import {SmModalStackService} from '../../shared/components/sm-confirmation-dialog/sm-modal-stack.service';
import {ContainersService, type ContainerListItem} from '../../containers/containers.service';
import type {SmNavBarProfileModel} from '../../shared/components/sm-nav-bar/sm-nav-bar.component';
import type {SmNavMenuItemModel} from '../../shared/components/sm-nav-menu-item/sm-nav-menu-item.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ShellBreadcrumbsService,
  isShellContainerBreadcrumb,
  type ShellBreadcrumb,
  type ShellContainerBreadcrumb,
} from './shell-breadcrumbs.service';
import {ShellBreadcrumbsComponent, type ShellBreadcrumbLongPressEvent} from './shell-breadcrumbs.component';
import {ItemsService} from '../../items/items.service';
import {APP_SHELL_COMPACT_MAX_WIDTH_PX} from '../../shared/breakpoints';
import {isNativeCapacitorPlatform} from '../../shared/platform';
import {ReportsActions} from '../../reports/store/reports.actions';
import {readDeviceTimeZoneId} from '../../reports/store/reports-calendar';
import {ApplicationIdentityService} from '../../shared/config/application-identity.service';
import {
  selectExpiredCount,
  selectExpiringSoonCount,
} from '../../reports/store/reports.selectors';
import {PublicationModerationService} from '../../moderation/publication-moderation.service';
import {
  ContainerRelocationDragService,
  type ContainerRelocationDragState,
} from '../../containers/container-relocation-drag.service';
import {sortByNaturalName} from '../../shared/text/natural-name-sort';
import {FavoritesService} from '../../favorites/favorites.service';
import {EditorActionsService} from '../../shared/layouts/editor-layout/editor-actions.service';
import {ExternalBrowserService} from '../../shared/external-browser.service';
import {buildGuidesNavigation} from './guides-navigation';
import {BasketService} from '../../basket/basket.service';
import {EventJournalCountService} from '../../events/event-journal-count.service';

type ShellNavItemDefinition = Omit<SmNavMenuItemModel, 'text' | 'children'> & {
  textKey: string;
  children?: ShellNavItemDefinition[];
};

type ShellSearchModeDefinition = Omit<SmContextMenuItemModel, 'text'> & {
  textKey: string;
};

const DESKTOP_NAVIGATION_STORAGE_KEY = 'invemory.desktop-navigation-expanded.v1';
const RELOCATION_SIBLING_HOVER_MS = 2000;
const RELOCATION_SIBLING_SCROLL_EDGE_PX = 56;
const RELOCATION_SIBLING_SCROLL_STEP_PX = 8;
const RELOCATION_SIBLING_SCROLL_INTERVAL_MS = 16;
const RELOCATION_REFRESH_RESTORE_DELAY_MS = 400;

@Component({
  selector: 'app-shell-layout',
  templateUrl: './shell-layout.component.html',
  styleUrls: ['./shell-layout.component.scss'],
  standalone: false,
  providers: [EditorActionsService],
})
export class ShellLayoutComponent implements OnInit, OnDestroy {
  private readonly journalCount = inject(EventJournalCountService);
  private reportsCountLatest: number | null = null;
  readonly modalStack = inject(SmModalStackService);
  readonly fullSearchOverlay = inject(SearchOverlayService);
  private readonly externalBrowser = inject(ExternalBrowserService);
  private openingGuide = false;
  readonly editorActions = inject(EditorActionsService);
  private readonly destroyRef = inject(DestroyRef);
  desktopNavigationExpanded = this.readDesktopNavigationExpanded();
  readonly isNativePlatform = isNativeCapacitorPlatform();
  @HostBinding('attr.data-build-marker')
  readonly buildMarkerLabel: string | null = environment.production ? null : 'APK DIAG';
  @HostBinding('class.app-shell--about')
  isAboutRoute = false;
  @ViewChild('searchStack') searchStack?: ElementRef<HTMLElement>;
  @ViewChild(ShellBreadcrumbsComponent) private shellBreadcrumbsComponent?: ShellBreadcrumbsComponent;
  breadcrumbs: ShellBreadcrumb[] = [];
  breadcrumbsCollapsed = false;
  breadcrumbTrailingActionTemplate: TemplateRef<unknown> | null = null;
  searchQuery = '';
  searchResultsLeft = 0;
  searchResultsWidth = 0;
  searchResultsTop = 0;
  searchResultsViewportTop = 0;
  selectedSearchModeId: GlobalSearchVisibilityMode = DEFAULT_GLOBAL_SEARCH_VISIBILITY_MODE;
  searchScannerOpen = false;
  searchScan: CodeScan | null = null;
  private readonly searchChangeDetector = inject(ChangeDetectorRef);
  searchResultsOpen = false;
  searchResultsLoading = false;
  searchResultsError = '';
  searchResults: GlobalSearchResults = {
    containers: [],
    hasMoreContainers: false,
    hasMoreItems: false,
    items: [],
  };

  navProfile: SmNavBarProfileModel = {
    email: '',
    roleLabel: '',
    avatarText: 'U',
  };
  private currentUser: CurrentUserResponse | null = null;
  private isAdministrator = false;
  private canModerate = false;
  private profileRequestId = 0;
  private profileRecoveryPending = false;
  private profileSession: AuthSession | null = null;

  navItems: SmNavMenuItemModel[] = [];
  quickNavItems: SmNavMenuItemModel[] = [];
  mainMenuOpen = false;
  navPreferenceAccountId = '';
  footerActionText = '';
  searchAriaLabel = '';
  searchPlaceholder = '';
  searchFiltersAriaLabel = '';
  searchScanAriaLabel = '';
  searchScannerTitle = '';
  searchScannerHint = '';
  searchScannerLoadingLabel = '';
  searchScannerUnsupportedLabel = '';
  searchScannerCloseAriaLabel = '';
  searchResultsTitle = '';
  searchResultsCloseLabel = '';
  searchResultsLoadingLabel = '';
  searchResultsLimitLabel = '';
  searchResultsEmptyTitle = '';
  searchResultsEmptyDescriptionTemplate = '';
  searchResultsErrorPrefix = '';
  searchResultsContainersTitle = '';
  searchResultsItemsTitle = '';
  searchResultsContainerEmptyDescriptionLabel = '';
  searchResultsItemEmptyDescriptionLabel = '';
  searchResultsContainerMenuAriaLabelTemplate = '';
  searchResultsItemMenuAriaLabelTemplate = '';
  searchResultsRootLocationLabel = '';
  searchResultsContainerMenuItems: SmContextMenuItemModel[] = [];
  searchResultsItemMenuItems: SmContextMenuItemModel[] = [];
  statusBarAriaLabel = '';
  breadcrumbAriaLabel = '';
  breadcrumbExpandLabel = '';
  breadcrumbFullPathLabel = '';
  breadcrumbBackToPathLabel = '';
  siblingSwitcherTitle = '';
  siblingSwitcherRelocationTitle = '';
  siblingSwitcherLoadingLabel = '';
  siblingSwitcherEmptyLabel = '';
  siblingSwitcherErrorLabel = '';
  siblingSwitcherRetryLabel = '';
  siblingSwitcherCurrentLabel = '';
  siblingSwitcherOpen = false;
  siblingSwitcherItems: ContainerListItem[] = [];
  siblingSwitcherResults: GlobalSearchContainerResult[] = [];
  siblingSwitcherLoading = false;
  siblingSwitcherError = '';
  siblingSwitcherCurrentContainerId = '';
  siblingSwitcherCurrentContainerName = '';
  siblingSwitcherPanelLeft = 0;
  siblingSwitcherPanelTop = 0;
  siblingSwitcherPanelWidth = 0;
  siblingSwitcherRelocationMode = false;
  siblingSwitcherRelocationSourceKind: 'container' | 'item' | null = null;
  siblingSwitcherRelocationSourceId = '';
  siblingSwitcherPathReturn = false;
  routeRefreshDisabled = false;
  private routeRefreshPending = false;
  private routeRefreshNavigationGeneration = 0;
  readonly getBreadcrumbHref = (route: string): string => this.buildRoutePath(route);
  readonly resolveBreadcrumbIcon = (breadcrumb: ShellBreadcrumb): string | null => {
    const route = stripLocalizedRouteLanguageFromPathname(breadcrumb.route);
    const queue = [...this.navItemDefinitions];
    let matchingIcon: string | null = null;
    let matchingLength = 0;
    while (queue.length) {
      const definition = queue.shift()!;
      queue.push(...(definition.children ?? []));
      if (definition.id === breadcrumb.id) return definition.iconName ?? null;
      if (definition.route && (route === definition.route || route.startsWith(`${definition.route}/`))
        && definition.route.length > matchingLength) {
        matchingIcon = definition.iconName ?? null;
        matchingLength = definition.route.length;
      }
    }
    return matchingIcon ?? 'navigate-outline';
  };
  searchModeItems: SmContextMenuItemModel[] = [];
  private readonly searchScheduler = new SearchScheduler();
  private qrLaunchRetryTimeoutId: number | null = null;
  private qrLaunchToken: string | null = null;
  private appStateListener?: PluginListenerHandle;
  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    this.resetBreadcrumbPresentation();
    if (this.currentUser) {
      this.store.dispatch(ReportsActions.applicationResumed());
      this.basketService.refreshNavigationCount();
    }
  };
  private siblingSwitcherBreadcrumb: ShellContainerBreadcrumb | null = null;
  private siblingSwitcherRequestId = 0;
  private siblingSwitcherOpeningPointerId: number | null = null;
  private siblingSwitcherSuppressCloseUntil = 0;
  activeRelocationDragState: ContainerRelocationDragState | null = null;
  breadcrumbPanelOwner: 'none' | 'path' | 'siblings' = 'none';
  private relocationSiblingHoverTimer: number | null = null;
  private relocationPathHoverTimer: number | null = null;
  private relocationPathHoverElement: HTMLElement | null = null;
  private relocationBackHoverTimer: number | null = null;
  private relocationBackHoverElement: HTMLElement | null = null;
  private relocationSiblingHoverBreadcrumbId: string | null = null;
  private relocationSiblingHoverElement: HTMLElement | null = null;
  private relocationExpandedBreadcrumbElement: HTMLElement | null = null;
  private relocationSiblingAutoScrollTimer: number | null = null;
  private relocationSiblingAutoScrollDirection: -1 | 1 | null = null;
  private relocationSiblingAutoScrollElement: HTMLElement | null = null;
  private relocationRefreshRestoreTimer: number | null = null;
  private readonly searchModeDefinitions: ShellSearchModeDefinition[] = [
    {
      id: 'withoutZeroCount',
      textKey: 'shell.nav.searchModes.available',
      iconName: 'checkmark-circle-outline',
    },
    {
      id: 'withZeroCount',
      textKey: 'shell.nav.searchModes.unavailable',
      iconName: 'close-circle-outline',
    },
    {
      id: 'all',
      textKey: 'shell.nav.searchModes.all',
      iconName: 'list-outline',
    },
  ];
  private readonly navItemDefinitions: ShellNavItemDefinition[] = [
    {
      id: 'home',
      textKey: 'shell.nav.items.home',
      iconName: 'home-outline',
      tone: 'blue',
      route: '/home',
    },
    {
      id: 'reports',
      textKey: 'shell.nav.items.reports',
      iconName: 'calendar-outline',
      tone: 'amber',
      children: [
        {
          id: 'reports-expiring',
          textKey: 'shell.nav.items.reportsExpiring',
          iconName: 'calendar-outline',
          badgeText: '0',
          tone: 'orange',
          route: '/reports/expiring',
        },
        {
          id: 'reports-expired',
          textKey: 'shell.nav.items.reportsExpired',
          iconName: 'trash-outline',
          badgeText: '0',
          tone: 'red',
          route: '/reports/expired',
        },
        {
          id: 'reports-latest',
          textKey: 'shell.nav.items.reportsLatest',
          iconName: 'newspaper-outline',
          tone: 'violet',
          route: '/reports/last-changes',
        },
      ],
    },
    {
      id: 'qr-codes',
      textKey: 'shell.nav.items.qrCodes',
      iconName: 'qr-code-outline',
      tone: 'slate',
      children: [
        {
          id: 'qr-codes-profiles',
          textKey: 'shell.nav.items.qrCodesProfiles',
          iconName: 'folder-open-outline',
          tone: 'blue',
          route: '/qr-labels/profiles',
        },
        {
          id: 'qr-codes-packages',
          textKey: 'shell.nav.items.qrCodesPackages',
          iconName: 'archive-outline',
          tone: 'slate',
          route: '/qr-labels/packages',
        },
      ],
    },
    {
      id: 'basket', textKey: 'basket.title', iconName: 'basket-outline', tone: 'navy', children: [
        {id: 'basket-needs', textKey: 'basket.needs', iconName: 'list-outline', tone: 'slate', route: '/basket'},
        {id: 'basket-shops', textKey: 'shops.title', iconName: 'storefront-outline', tone: 'slate', route: '/basket/shops'},
      ],
    },
    {
      id: 'inventory',
      textKey: 'shell.nav.items.inventory',
      iconName: 'cube-outline',
      tone: 'mint',
      children: [
        {
          id: 'inventory-containers',
          textKey: 'shell.nav.items.inventoryContainers',
          iconName: 'file-tray-outline',
          tone: 'mint',
          route: '/inventory/containers',
        },
        {
          id: 'inventory-favorites',
          textKey: 'favorites.title',
          iconName: 'star-outline',
          tone: 'mint',
          route: '/inventory/favorites',
        },
        {
          id: 'inventory-transfers',
          textKey: 'shell.nav.items.inventoryTransfers',
          iconName: 'inventory-move',
          tone: 'mint',
          route: '/relocation',
        },
        {
          id: 'inventory-writeoffs',
          textKey: 'shell.nav.items.inventoryWriteOffs',
          iconName: 'remove-circle-outline',
          tone: 'rose',
          route: '/write-off',
        },
      ],
    },
    {
      id: 'people',
      textKey: 'shell.nav.items.people',
      iconName: 'person-outline',
      tone: 'slate',
      children: [
        {
          id: 'people-received-invites',
          textKey: 'shell.nav.items.peopleReceivedInvites',
          iconName: 'mail-outline',
          tone: 'blue',
          route: '/people/received',
        },
        {
          id: 'people-sent-invites',
          textKey: 'shell.nav.items.peopleSentInvites',
          iconName: 'send-outline',
          tone: 'mint',
          route: '/people/sent',
        },
        {
          id: 'people-trusted-contacts',
          textKey: 'shell.nav.items.peopleTrustedContacts',
          iconName: 'shield-outline',
          tone: 'green',
          route: '/people/trusted-contacts',
        },
      ],
    },
    {
      id: 'feedback',
      textKey: 'shell.nav.items.feedback',
      iconName: 'chatbubble-ellipses-outline',
      tone: 'blue',
      route: '/feedback',
    },
    {
      id: 'moderation',
      textKey: 'shell.nav.items.moderation',
      iconName: 'shield-checkmark-outline',
      tone: 'amber',
      badgeText: '0',
      route: '/moderation/publication-requests',
    },
    {
      id: 'administration',
      textKey: 'shell.nav.items.administration',
      iconName: 'lock-closed-outline',
      tone: 'navy',
      children: [
        {
          id: 'administration-users',
          textKey: 'shell.nav.items.administrationUsers',
          iconName: 'people-outline',
          tone: 'navy',
          route: '/administration/users',
        },
        {
          id: 'administration-feedback',
          textKey: 'shell.nav.items.administrationFeedback',
          iconName: 'chatbubble-ellipses-outline',
          tone: 'blue',
          route: '/administration/feedback',
        },
        {
          id: 'administration-operational-settings',
          textKey: 'shell.nav.items.administrationOperationalSettings',
          iconName: 'options-outline',
          tone: 'slate',
          route: '/administration/operational-settings',
        },
        {id: 'administration-audits', textKey: 'auditsPage.title', iconName: 'analytics-outline', tone: 'slate', route: '/administration/audits'},
        {id: 'administration-appearance', textKey: 'appearance.admin.title', iconName: 'color-palette-outline', tone: 'slate', route: '/administration/appearance'},
        {id: 'administration-releases', textKey: 'administrationReleasesPage.title', iconName: 'newspaper-outline', tone: 'slate', route: '/administration/releases'},
        {
          id: 'administration-payment-providers',
          textKey: 'shell.nav.items.administrationPaymentProviders',
          iconName: 'card-outline',
          tone: 'slate',
          route: '/administration/payment-providers',
        },
        {
          id: 'administration-pricing-plans',
          textKey: 'shell.nav.items.administrationPricingPlans',
          iconName: 'pricetags-outline',
          tone: 'slate',
          route: '/administration/pricing-plans',
        },
      ],
    },
    {
      id: 'profile',
      textKey: 'shell.nav.items.profile',
      iconName: 'person-circle-outline',
      tone: 'navy',
      children: [
        {
          id: 'profile-settings',
          textKey: 'shell.nav.items.profileSettings',
          iconName: 'options-outline',
          tone: 'slate',
          route: '/profile/settings',
        },
        {id: 'profile-mailings', textKey: 'mailings.title', iconName: 'mail-outline', tone: 'slate', route: '/profile/mailings'},
        {id: 'profile-appearance', textKey: 'appearance.title', iconName: 'color-palette-outline', tone: 'slate', route: '/profile/appearance'},

        {
          id: 'profile-subscription',
          textKey: 'customerBilling.title',
          iconName: 'card-outline',
          tone: 'slate',
          route: '/profile/subscription',
        },
          {
            id: 'profile-onboarding',
            textKey: 'shell.nav.items.profileOnboarding',
            iconName: 'sparkles-outline',
            tone: 'slate',
            route: '/profile/onboarding',
          },
        ],
    },
    {
      id: 'about',
      textKey: 'shell.nav.items.about',
      iconName: 'information-circle-outline',
      tone: 'slate',
      route: '/about',
    },
  ];

  selectedNavId = 'home';

  constructor(
    private readonly authService: AuthService,
    private readonly apiCompatibility: ApiCompatibilityService,
    private readonly router: Router,
    private readonly menuController: MenuController,
    private readonly translateService: TranslateService,
    private readonly ngZone: NgZone,
    private readonly appLanguageService: AppLanguageService,
    private readonly shellBreadcrumbsService: ShellBreadcrumbsService,
    private readonly globalSearchService: GlobalSearchService,
    private readonly fullSearchState: SearchStateService,
    private readonly containersService: ContainersService,
    private readonly itemsService: ItemsService,
    private readonly confirmationDialogService: SmConfirmationDialogService,
    private readonly publicationModerationService: PublicationModerationService,
    private readonly store: Store,
    readonly applicationIdentityService: ApplicationIdentityService,
    private readonly analyticsService: AnalyticsService,
    private readonly containerRelocationDragService: ContainerRelocationDragService,
    private readonly favoritesService: FavoritesService,
    private readonly basketService: BasketService,
  ) {
    this.apiCompatibility.recovered$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.profileRecoveryPending && this.profileSession) void this.syncProfileForSession(this.profileSession);
    });
    this.updateRouteUiState(this.router.url);
    this.refreshLocalizedContent();

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.siblingSwitcherItems = this.sortNamedContainers(this.siblingSwitcherItems);
        this.siblingSwitcherResults = this.sortNamedContainers(this.siblingSwitcherResults);
        this.refreshLocalizedContent();
      });

    combineLatest([
      this.store.select(selectExpiringSoonCount),
      this.store.select(selectExpiredCount),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([expiring, expired]) => {
        this.reportsCountExpiring = expiring;
        this.reportsCountExpired = expired;
        this.rebuildNavItems();
      });

    this.publicationModerationService.pendingCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.moderationPendingCount = count;
        this.rebuildNavItems();
      });

    this.journalCount.count$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this.reportsCountLatest = count;
      this.rebuildNavItems();
    });

    this.basketService.navigationCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.basketOutstandingCount = count;
        this.rebuildNavItems();
      });

    this.basketService.changes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.basketService.refreshNavigationCount());

    this.authService.session$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((session) => {
        this.navPreferenceAccountId = session?.user.id ?? '';
        this.basketService.setNavigationAccount(session?.user.id ?? '');
        if (session) this.basketService.refreshNavigationCount();
        void this.syncProfileForSession(session);
      });

    this.router.events
      .pipe(
        tap(event => { if (event instanceof NavigationStart) this.routeRefreshNavigationGeneration += 1; }),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.searchScheduler.invalidate();
        this.cancelSearchTimeout();
        this.searchScannerOpen = false;
        this.updateRouteUiState(event.urlAfterRedirects);
        if (this.navPreferenceAccountId) this.basketService.refreshNavigationCount();
      });

    this.shellBreadcrumbsService.breadcrumbs$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((breadcrumbs) => {
        this.breadcrumbs = breadcrumbs;
      });

    this.shellBreadcrumbsService.trailingActionTemplate$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((template) => {
        this.breadcrumbTrailingActionTemplate = template;
      });

    this.containerRelocationDragService.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.handleRelocationDragState(state);
      });

    this.favoritesService.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((change) => {
        if (change.entityType !== 'container') {
          return;
        }

        this.siblingSwitcherItems = this.siblingSwitcherItems.map((container) =>
          container.id === change.id ? {...container, isFavorite: change.isFavorite} : container,
        );
        this.siblingSwitcherResults = this.siblingSwitcherResults.map((container) =>
          container.id === change.id ? {...container, isFavorite: change.isFavorite} : container,
        );
      });

  }

  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (!this.isNativePlatform) {
      return;
    }

    void this.configureQrLaunchRecovery();
  }

  get menuMaxEdgeStart(): number {
    return this.isNativePlatform || this.isCompactViewport() ? 99999 : 50;
  }

  handleSearchQueryChange(nextQuery: string): void {
    this.searchScan = null;
    this.searchQuery = nextQuery;
    this.searchResultsOpen = nextQuery.trim().length >= 2;
    this.searchChangeDetector.detectChanges();
    this.updateSearchResultsLayout();
    this.scheduleSearch();
  }

  handleSearchModeChange(nextModeId: string): void {
    this.selectedSearchModeId = nextModeId === 'withZeroCount' || nextModeId === 'all'
      ? nextModeId
      : DEFAULT_GLOBAL_SEARCH_VISIBILITY_MODE;
    this.scheduleSearch();
  }

  handleSearchSubmit(): void {
    this.cancelSearchTimeout();
    this.searchResultsOpen = this.searchQuery.trim().length >= 2;
    this.searchChangeDetector.detectChanges();
    this.updateSearchResultsLayout();
    void this.performSearch();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.searchResultsOpen) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      this.searchResultsOpen = false;
      return;
    }

    if (this.searchStack?.nativeElement.contains(target)) {
      return;
    }

    this.searchResultsOpen = false;
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.updateSearchResultsLayout();
    this.shellBreadcrumbsComponent?.refreshFullPathPosition();
    if (this.shellBreadcrumbsComponent?.isRelocationFullPathOpen()) {
      this.shellBreadcrumbsComponent.closeRelocationFullPath();
      this.shellBreadcrumbsComponent.openRelocationFullPath();
    }
    this.resetBreadcrumbPresentation();
  }

  async handleNavItemSelected(item: SmNavMenuItemModel): Promise<void> {
    if (item.externalHref) {
      if (this.isNativePlatform) {
        await this.openGuide(item.externalHref);
      }
      return;
    }
    this.selectedNavId = item.id;
    await this.closeMainMenuIfMobileOverlay();
  }

  isDesktopViewport(): boolean {
    return typeof window !== 'undefined' && (window.matchMedia('(min-width: 992px)')?.matches ?? false);
  }

  toggleDesktopNavigation(dockedSearch = false): void {
    if (dockedSearch) {
      this.fullSearchOverlay.navigationVisible = !this.fullSearchOverlay.navigationVisible;
      return;
    }
    this.desktopNavigationExpanded = !this.desktopNavigationExpanded;
    try {
      window.localStorage.setItem(DESKTOP_NAVIGATION_STORAGE_KEY, String(this.desktopNavigationExpanded));
    } catch {
      // Navigation remains usable when browser storage is unavailable.
    }
  }

  private readDesktopNavigationExpanded(): boolean {
    try {
      return typeof window === 'undefined' || window.localStorage.getItem(DESKTOP_NAVIGATION_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  }

  async handleFooterActionClick(): Promise<void> {
    await this.authService.logout();
    const loginPath = `${this.buildRoutePath('/login')}?appearanceSource=logout`;

    if (typeof window !== 'undefined' && typeof window.location.replace === 'function') {
      window.location.replace(loginPath);
      return;
    }

    await this.router.navigateByUrl(loginPath, {replaceUrl: true});
  }

  async handleRouteRefresh(event?: RefresherCustomEvent): Promise<void> {
    if (this.routeRefreshDisabled || this.routeRefreshPending || this.destroyRef.destroyed) {
      event?.detail.complete();
      return;
    }
    this.routeRefreshPending = true;
    const url = this.router.url;
    const navigationGeneration = this.routeRefreshNavigationGeneration;
    try {
      if (!await this.editorActions.requestRefresh()) return;
      const state = await this.apiCompatibility.recheck();
      if (state === 'compatible' && this.isCurrentRefreshTarget(url, navigationGeneration)) {
        this.reloadCurrentPage();
      }
    } catch {
      // Compatibility owns failure presentation; an unfinished check must retain the view.
    } finally {
      this.routeRefreshPending = false;
      event?.detail.complete();
    }
  }

  private isCurrentRefreshTarget(url: string, navigationGeneration: number): boolean {
    return !this.destroyRef.destroyed && !this.routeRefreshDisabled
      && navigationGeneration === this.routeRefreshNavigationGeneration && url === this.router.url;
  }

  handleRouteScroll(_event: ScrollCustomEvent): void {}

  ngOnDestroy(): void {
    this.searchScheduler.invalidate();
    this.cancelRelocationSiblingHover();
    this.cancelRelocationPathHover();
    this.cancelRelocationBackHover();
    this.stopRelocationSiblingAutoScroll();
    this.shellBreadcrumbsComponent?.closeRelocationFullPath();
    if (this.siblingSwitcherOpen) this.handleSiblingSwitcherDismiss();
    this.cancelRelocationRefreshRestore();
    this.cancelSearchTimeout();
    this.cancelQrLaunchRetryTimeout();
    void this.appStateListener?.remove();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  async handleSearchScanClick(): Promise<void> {
    this.searchScannerOpen = true;
  }

  handleSearchScannerClose(): void {
    this.searchScannerOpen = false;
  }

  handleSearchScannerDetected(scan: CodeScan): void {
    this.searchScannerOpen = false;
    this.handleSearchQueryChange(scan.value);
    this.searchScan = scan;
    this.searchChangeDetector.detectChanges();
    this.updateSearchResultsLayout();
    this.cancelSearchTimeout();
    void this.performSearch();
  }

  handleSearchResultsClose(): void {
    this.searchScheduler.invalidate();
    this.searchResultsOpen = false;
    this.searchResultsLoading = false;
    this.searchResultsError = '';
    this.cancelSearchTimeout();
    this.cancelQrLaunchRetryTimeout();
    this.qrLaunchToken = null;
  }

  openFullSearch(): void {
    this.fullSearchState.enter(this.searchQuery, this.selectedSearchModeId, this.searchScan?.type ?? 'text', this.searchTagSelection);
    this.handleSearchResultsClose();
    void this.router.navigateByUrl(this.buildRoutePath('/search'));
  }

  handleSearchContainerOpen(result: GlobalSearchContainerResult): void {
    this.searchResultsOpen = false;

    void this.router.navigateByUrl(this.buildRoutePath(`/inventory/containers/${result.id}`), {
      state: {
        containerPath: result.path.map((segment) => ({
          id: segment.id,
          name: segment.name,
        })),
      },
    });
  }

  handleSearchItemOpen(result: GlobalSearchItemResult): void {
    this.searchResultsOpen = false;
    const locationId = result.locationId || result.containerId;

    void this.router.navigateByUrl(
      this.buildRoutePath(`/inventory/containers/${locationId}/items/${result.id}/details`),
      {
        state: {
          containerPath: result.containerPath.map((segment) => ({
            id: segment.id,
            name: segment.name,
          })),
        },
      },
    );
  }

  handleSearchContainerMenuItemSelect(event: {item: SmContextMenuItemModel; result: GlobalSearchContainerResult}): void {
    const containerPath = event.result.path.map((segment) => ({id: segment.id, name: segment.name}));
    void handleContainerActionMenuItemSelect(this.router, event.item, {
      containerPath,
      languageCode: this.appLanguageService.languageCode,
      returnUrl: this.router.url,
      closeMenu: () => {
        this.searchResultsOpen = false;
      },
      afterDelete: async () => {
        const pathname = stripLocalizedRouteLanguageFromPathname(this.router.url.split(/[?#]/)[0]);
        if (pathname === `/inventory/containers/${event.result.id}`) {
          const parentPath = containerPath.slice(0, -1);
          const parentId = parentPath[parentPath.length - 1]?.id;
          const target = parentId ? `/inventory/containers/${parentId}` : '/inventory/containers';
          await this.router.navigateByUrl(this.buildRoutePath(target), {state: {containerPath: parentPath}});
        }
        this.scheduleSearch();
      },
    }, {
      id: event.result.id,
      name: event.result.name,
      description: event.result.description,
      imageUrl: event.result.imageUrl,
    }, {
      confirmationDialogService: this.confirmationDialogService,
      containersService: this.containersService,
      translateService: this.translateService,
    });
  }

  handleSearchItemMenuItemSelect(event: {item: SmContextMenuItemModel; result: GlobalSearchItemResult}): void {
    const locationId = event.result.locationId || event.result.containerId;
    void handleItemActionMenuItemSelect(this.router, event.item, {
      containerId: locationId,
      currentPath: event.result.containerPath.map((segment) => ({
        id: segment.id,
        name: segment.name,
      })),
      containerName: event.result.containerName,
      languageCode: this.appLanguageService.languageCode,
      returnUrl: this.router.url,
      closeMenu: () => {
        this.searchResultsOpen = false;
      },
      afterDelete: async () => {
        this.scheduleSearch();
      },
    }, {
      id: event.result.id,
      name: event.result.name,
      description: event.result.description,
      imageUrl: event.result.imageUrl,
      quantity: event.result.quantity,
    }, {
      confirmationDialogService: this.confirmationDialogService,
      itemsService: this.itemsService,
      translateService: this.translateService,
    });
  }

  isCompactViewport(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia(`(max-width: ${APP_SHELL_COMPACT_MAX_WIDTH_PX}px)`)?.matches ?? false;
  }

  async navigateToBreadcrumb(route: string): Promise<void> {
    const breadcrumbTrail = this.buildContainerBreadcrumbTrail(route);
    if (breadcrumbTrail) {
      const navigated = await this.router.navigateByUrl(this.buildRoutePath(route), {
        state: {
          containerPath: breadcrumbTrail.slice(2).map((breadcrumb) => ({
            id: breadcrumb.id,
            name: breadcrumb.label,
          })),
        },
      });
      if (navigated) this.shellBreadcrumbsService.setBreadcrumbs(breadcrumbTrail);
      return;
    }

    await this.router.navigateByUrl(this.buildRoutePath(route));
  }

  async handleBreadcrumbLongPress(event: ShellBreadcrumbLongPressEvent): Promise<void> {
    const fromPath = this.breadcrumbPanelOwner === 'path';
    if (fromPath) await this.shellBreadcrumbsComponent?.closeNavigationFullPath();
    this.siblingSwitcherPathReturn = fromPath;
    this.openSiblingSwitcher(
      event.breadcrumb,
      event.event.clientX,
      event.event.clientY,
      event.event.pointerId,
    );
  }

  handleFullPathOpened(): void {
    if (this.siblingSwitcherOpen) this.handleSiblingSwitcherDismiss();
    this.breadcrumbPanelOwner = 'path';
  }

  handleFullPathClosed(): void {
    if (this.breadcrumbPanelOwner === 'path') this.breadcrumbPanelOwner = 'none';
  }

  handleSiblingSwitcherBackToPath(): void {
    if (!this.siblingSwitcherPathReturn || this.activeRelocationDragState) return;
    this.handleSiblingSwitcherDismiss();
    this.shellBreadcrumbsComponent?.reopenNavigationFullPath();
  }

  @HostListener('document:pointerup', ['$event'])
  handleSiblingSwitcherOpeningPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.siblingSwitcherOpeningPointerId) {
      return;
    }

    this.siblingSwitcherOpeningPointerId = null;
    this.siblingSwitcherSuppressCloseUntil = Date.now() + 400;
  }

  @HostListener('document:pointercancel', ['$event'])
  handleSiblingSwitcherOpeningPointerCancel(event: PointerEvent): void {
    if (event.pointerId === this.siblingSwitcherOpeningPointerId) {
      this.siblingSwitcherOpeningPointerId = null;
    }
  }

  handleSiblingSwitcherCloseRequested(): void {
    if (this.siblingSwitcherRelocationMode) {
      return;
    }

    if (Date.now() < this.siblingSwitcherSuppressCloseUntil) {
      return;
    }

    this.handleSiblingSwitcherDismiss();
  }

  handleSiblingSwitcherDismiss(): void {
    this.siblingSwitcherOpen = false;
    if (this.breadcrumbPanelOwner === 'siblings') this.breadcrumbPanelOwner = 'none';
    this.resetSiblingSwitcherState();
  }

  private resetSiblingSwitcherState(): void {
    this.cancelRelocationBackHover();
    this.relocationExpandedBreadcrumbElement?.removeAttribute('data-relocation-drop-disabled');
    this.relocationExpandedBreadcrumbElement = null;
    this.stopRelocationSiblingAutoScroll();
    this.siblingSwitcherBreadcrumb = null;
    this.siblingSwitcherItems = [];
    this.siblingSwitcherResults = [];
    this.siblingSwitcherLoading = false;
    this.siblingSwitcherError = '';
    this.siblingSwitcherCurrentContainerId = '';
    this.siblingSwitcherCurrentContainerName = '';
    this.siblingSwitcherOpeningPointerId = null;
    this.siblingSwitcherSuppressCloseUntil = 0;
    this.siblingSwitcherRelocationMode = false;
    this.siblingSwitcherRelocationSourceKind = null;
    this.siblingSwitcherRelocationSourceId = '';
    this.siblingSwitcherPathReturn = false;
    this.siblingSwitcherRequestId += 1;
  }

  retrySiblingSwitcher(): void {
    this.loadSiblingContainers();
  }

  handleSiblingContainerOpen(result: GlobalSearchContainerResult): void {
    if (this.siblingSwitcherRelocationMode) {
      return;
    }

    const container = this.siblingSwitcherItems.find((candidate) => candidate.id === result.id);
    if (container) {
      this.selectSiblingContainer(container);
    }
  }

  handleSiblingContainerMenuItemSelect(event: {item: SmContextMenuItemModel; result: GlobalSearchContainerResult}): void {
    if (this.siblingSwitcherRelocationMode) {
      return;
    }

    const breadcrumb = this.siblingSwitcherBreadcrumb;
    const container = this.siblingSwitcherItems.find((candidate) => candidate.id === event.result.id);
    if (!breadcrumb || !container) {
      return;
    }

    const parentNavigation = breadcrumb.containerNavigation;
    const selectedPath = event.result.path.map((segment) => ({id: segment.id, name: segment.name}));
    const isCurrent = container.id === breadcrumb.id;
    void handleContainerActionMenuItemSelect(this.router, event.item, {
      containerPath: selectedPath,
      languageCode: this.appLanguageService.languageCode,
      returnUrl: this.router.url,
      closeMenu: () => this.handleSiblingSwitcherDismiss(),
      afterDelete: async () => {
        if (!isCurrent) {
          return;
        }

        const parentId = parentNavigation.parentContainerId;
        const target = parentId
          ? `/inventory/containers/${parentId}`
          : '/inventory/containers';
        await this.router.navigateByUrl(this.buildRoutePath(target), {
          state: {containerPath: parentNavigation.parentPath},
        });
      },
    }, container, {
      confirmationDialogService: this.confirmationDialogService,
      containersService: this.containersService,
      translateService: this.translateService,
    });
  }

  selectSiblingContainer(container: ContainerListItem): void {
    const breadcrumb = this.siblingSwitcherBreadcrumb;
    if (!breadcrumb) {
      return;
    }

    const containerNavigation = breadcrumb.containerNavigation;

    if (container.id === breadcrumb.id) {
      this.handleSiblingSwitcherDismiss();
      return;
    }

    const containerPath = [
      ...containerNavigation.parentPath,
      {id: container.id, name: container.name},
    ];
    this.handleSiblingSwitcherDismiss();
    void this.router.navigateByUrl(
      this.buildRoutePath(`/inventory/containers/${container.id}`),
      {state: {containerPath}},
    );
  }

  private openSiblingSwitcher(
    breadcrumb: ShellContainerBreadcrumb,
    anchorX: number,
    anchorY: number,
    openingPointerId: number | null,
  ): void {
    this.cancelRelocationPathHover();
    this.stopRelocationSiblingAutoScroll();
    this.shellBreadcrumbsComponent?.closeRelocationFullPath();
    this.breadcrumbPanelOwner = 'siblings';
    this.siblingSwitcherBreadcrumb = breadcrumb;
    this.siblingSwitcherCurrentContainerId = breadcrumb.id;
    this.siblingSwitcherCurrentContainerName = breadcrumb.label;
    this.siblingSwitcherOpeningPointerId = openingPointerId;
    this.positionSiblingSwitcher(anchorX, anchorY);
    this.siblingSwitcherOpen = true;
    this.loadSiblingContainers();
  }

  private handleRelocationDragState(state: ContainerRelocationDragState | null): void {
    const wasActive = this.activeRelocationDragState !== null;
    this.activeRelocationDragState = state;
    if (state) {
      this.cancelRelocationRefreshRestore();
      this.routeRefreshDisabled = true;
    } else if (wasActive) {
      this.scheduleRelocationRefreshRestore();
    }

    if (!state) {
      this.cancelRelocationSiblingHover();
      this.cancelRelocationPathHover();
      this.cancelRelocationBackHover();
      this.stopRelocationSiblingAutoScroll();
      this.shellBreadcrumbsComponent?.closeRelocationFullPath();
      if (this.siblingSwitcherRelocationMode) {
        this.handleSiblingSwitcherDismiss();
      }
      return;
    }

    if (this.siblingSwitcherRelocationMode) {
      const nextBreadcrumb = this.findRelocationSiblingBreadcrumb(state.clientX, state.clientY);
      if (nextBreadcrumb && nextBreadcrumb.breadcrumb.id !== this.siblingSwitcherBreadcrumb?.id) {
        this.siblingSwitcherPathReturn = false;
        this.handleSiblingSwitcherDismiss();
        this.startRelocationSiblingHover(nextBreadcrumb.breadcrumb, nextBreadcrumb.element);
        return;
      }

      this.updateRelocationBackHover(state.clientX, state.clientY);
      this.updateRelocationSiblingAutoScroll(state.clientX, state.clientY);
      return;
    }

    const target = this.findRelocationSiblingBreadcrumb(state.clientX, state.clientY);
    this.updateRelocationPathOrSiblingHover(state.clientX, state.clientY, target);
    this.updateRelocationSiblingAutoScroll(state.clientX, state.clientY);
  }

  private updateRelocationPathOrSiblingHover(
    clientX: number,
    clientY: number,
    target = this.findRelocationSiblingBreadcrumb(clientX, clientY),
  ): void {
    if (target) {
      this.cancelRelocationPathHover();
      this.startRelocationSiblingHover(target.breadcrumb, target.element);
    } else {
      this.cancelRelocationSiblingHover();
      this.updateRelocationPathHover(clientX, clientY);
    }
  }

  private updateRelocationPathHover(clientX: number, clientY: number): void {
    if (this.shellBreadcrumbsComponent?.isRelocationFullPathOpen()) return;
    const element = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('[data-relocation-full-path-trigger]') ?? null;
    if (!element) { this.cancelRelocationPathHover(); return; }
    if (this.relocationPathHoverElement === element) return;
    this.cancelRelocationPathHover();
    this.relocationPathHoverElement = element;
    element.classList.add('is-relocation-path-trigger');
    this.relocationPathHoverTimer = window.setTimeout(() => {
      if (!this.activeRelocationDragState || this.relocationPathHoverElement !== element) return;
      this.cancelRelocationPathHover();
      this.shellBreadcrumbsComponent?.openRelocationFullPath();
    }, RELOCATION_SIBLING_HOVER_MS);
  }

  private cancelRelocationPathHover(): void {
    if (this.relocationPathHoverTimer !== null) window.clearTimeout(this.relocationPathHoverTimer);
    this.relocationPathHoverTimer = null;
    this.relocationPathHoverElement?.classList.remove('is-relocation-path-trigger');
    this.relocationPathHoverElement = null;
  }

  private updateRelocationBackHover(clientX: number, clientY: number): void {
    if (!this.siblingSwitcherPathReturn) return;
    const element = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('[data-relocation-path-back]') ?? null;
    if (!element) { this.cancelRelocationBackHover(); return; }
    if (this.relocationBackHoverElement === element) return;
    this.cancelRelocationBackHover();
    this.relocationBackHoverElement = element;
    element.classList.add('is-relocation-path-back');
    this.relocationBackHoverTimer = window.setTimeout(() => {
      if (!this.activeRelocationDragState || this.relocationBackHoverElement !== element) return;
      this.cancelRelocationBackHover();
      this.handleSiblingSwitcherDismiss();
      this.shellBreadcrumbsComponent?.openRelocationFullPath();
    }, RELOCATION_SIBLING_HOVER_MS);
  }

  private cancelRelocationBackHover(): void {
    if (this.relocationBackHoverTimer !== null) window.clearTimeout(this.relocationBackHoverTimer);
    this.relocationBackHoverTimer = null;
    this.relocationBackHoverElement?.classList.remove('is-relocation-path-back');
    this.relocationBackHoverElement = null;
  }

  private findRelocationSiblingBreadcrumb(
    clientX: number,
    clientY: number,
  ): {breadcrumb: ShellContainerBreadcrumb; element: HTMLElement} | null {
    const elementAtPoint = document.elementFromPoint(clientX, clientY);
    const element = elementAtPoint?.closest<HTMLElement>('[data-sibling-switcher-breadcrumb-id]') ?? null;
    const breadcrumbId = element?.dataset['siblingSwitcherBreadcrumbId'] ?? null;
    const breadcrumb = breadcrumbId
      ? this.breadcrumbs.find((candidate) => candidate.id === breadcrumbId)
      : null;
    if (!element || !breadcrumb || !isShellContainerBreadcrumb(breadcrumb)) {
      return null;
    }

    return {breadcrumb, element};
  }

  private startRelocationSiblingHover(
    breadcrumb: ShellContainerBreadcrumb,
    breadcrumbElement: HTMLElement,
  ): void {
    if (this.relocationSiblingHoverBreadcrumbId === breadcrumb.id) {
      return;
    }

    this.cancelRelocationSiblingHover();
    this.cancelRelocationBackHover();
    this.relocationSiblingHoverBreadcrumbId = breadcrumb.id;
    this.relocationSiblingHoverElement = breadcrumbElement;
    breadcrumbElement.classList.add('is-relocation-sibling-trigger');
    this.relocationSiblingHoverTimer = window.setTimeout(() => {
      this.openRelocationSiblingSwitcher(breadcrumb, breadcrumbElement);
    }, RELOCATION_SIBLING_HOVER_MS);
  }

  private openRelocationSiblingSwitcher(
    breadcrumb: ShellContainerBreadcrumb,
    breadcrumbElement: HTMLElement,
  ): void {
    const state = this.activeRelocationDragState;
    if (!state || this.relocationSiblingHoverBreadcrumbId !== breadcrumb.id) {
      return;
    }

    this.cancelRelocationSiblingHover();
    this.siblingSwitcherRelocationMode = true;
    this.siblingSwitcherRelocationSourceKind = state.source.kind;
    this.siblingSwitcherRelocationSourceId = state.source.id;
    this.siblingSwitcherPathReturn = this.breadcrumbs.length > 2 && this.isCompactViewport();
    this.relocationExpandedBreadcrumbElement = breadcrumbElement;
    breadcrumbElement.dataset['relocationDropDisabled'] = 'true';
    breadcrumbElement.classList.remove('is-relocation-drop-target--active');
    const bounds = breadcrumbElement.getBoundingClientRect();
    this.openSiblingSwitcher(breadcrumb, bounds.left, bounds.bottom, null);
  }

  private cancelRelocationSiblingHover(): void {
    if (this.relocationSiblingHoverTimer !== null) {
      window.clearTimeout(this.relocationSiblingHoverTimer);
      this.relocationSiblingHoverTimer = null;
    }

    this.relocationSiblingHoverElement?.classList.remove('is-relocation-sibling-trigger');
    this.relocationSiblingHoverElement = null;
    this.relocationSiblingHoverBreadcrumbId = null;
  }

  private updateRelocationSiblingAutoScroll(clientX: number, clientY: number): void {
    const scroller = document.querySelector<HTMLElement>('[data-relocation-sibling-scroll], [data-relocation-full-path-scroll]');
    if (!scroller) {
      this.stopRelocationSiblingAutoScroll();
      return;
    }

    const bounds = scroller.getBoundingClientRect();
    if (clientX < bounds.left || clientX > bounds.right || clientY < bounds.top || clientY > bounds.bottom) {
      this.stopRelocationSiblingAutoScroll();
      return;
    }

    const direction = clientY <= bounds.top + RELOCATION_SIBLING_SCROLL_EDGE_PX
      ? -1
      : clientY >= bounds.bottom - RELOCATION_SIBLING_SCROLL_EDGE_PX
        ? 1
        : null;
    if (direction === this.relocationSiblingAutoScrollDirection
      && scroller === this.relocationSiblingAutoScrollElement) {
      return;
    }

    this.stopRelocationSiblingAutoScroll();
    if (!direction) {
      return;
    }

    this.relocationSiblingAutoScrollDirection = direction;
    this.relocationSiblingAutoScrollElement = scroller;
    this.relocationSiblingAutoScrollTimer = window.setInterval(() => {
      const state = this.activeRelocationDragState;
      if (!state || !scroller.isConnected || scroller !== this.relocationSiblingAutoScrollElement) {
        this.stopRelocationSiblingAutoScroll();
        return;
      }
      scroller.scrollBy(0, direction * RELOCATION_SIBLING_SCROLL_STEP_PX);
      if (!this.siblingSwitcherRelocationMode) {
        this.updateRelocationPathOrSiblingHover(state.clientX, state.clientY);
      }
    }, RELOCATION_SIBLING_SCROLL_INTERVAL_MS);
  }

  private stopRelocationSiblingAutoScroll(): void {
    if (this.relocationSiblingAutoScrollTimer !== null) {
      window.clearInterval(this.relocationSiblingAutoScrollTimer);
      this.relocationSiblingAutoScrollTimer = null;
    }

    this.relocationSiblingAutoScrollDirection = null;
    this.relocationSiblingAutoScrollElement = null;
  }

  private scheduleRelocationRefreshRestore(): void {
    this.cancelRelocationRefreshRestore();
    this.routeRefreshDisabled = true;
    this.relocationRefreshRestoreTimer = window.setTimeout(() => {
      this.relocationRefreshRestoreTimer = null;
      this.routeRefreshDisabled = false;
    }, RELOCATION_REFRESH_RESTORE_DELAY_MS);
  }

  private cancelRelocationRefreshRestore(): void {
    if (this.relocationRefreshRestoreTimer !== null) {
      window.clearTimeout(this.relocationRefreshRestoreTimer);
      this.relocationRefreshRestoreTimer = null;
    }
  }

  private refreshLocalizedContent(): void {
    this.rebuildNavItems();
    this.navProfile = this.buildNavProfile(this.currentUser, this.navProfile.email);
    this.footerActionText = this.translateService.instant('shell.nav.footerAction');
    this.searchAriaLabel = this.translateService.instant('shell.nav.searchAriaLabel');
    this.searchPlaceholder = this.translateService.instant('shell.nav.searchPlaceholder');
    this.searchFiltersAriaLabel = this.translateService.instant('shell.nav.searchFiltersAriaLabel');
    this.searchScanAriaLabel = this.translateService.instant('shell.nav.searchScanAriaLabel');
    this.searchScannerTitle = this.translateService.instant('shell.nav.searchScannerTitle');
    this.searchScannerHint = this.translateService.instant('shell.nav.searchScannerHint');
    this.searchScannerLoadingLabel = this.translateService.instant('shell.nav.searchScannerLoadingLabel');
    this.searchScannerUnsupportedLabel = this.translateService.instant('shell.nav.searchScannerUnsupportedLabel');
    this.searchScannerCloseAriaLabel = this.translateService.instant('shell.nav.searchScannerCloseAriaLabel');
    this.searchResultsTitle = this.translateService.instant('shell.nav.searchResultsTitle');
    this.searchResultsCloseLabel = this.translateService.instant('auth.common.closeButton');
    this.searchResultsLoadingLabel = this.translateService.instant('shell.nav.searchResultsLoadingLabel');
    this.searchResultsLimitLabel = this.translateService.instant('shell.nav.searchResultsLimitLabel');
    this.searchResultsEmptyTitle = this.translateService.instant('shell.nav.searchResultsEmptyTitle');
    this.searchResultsEmptyDescriptionTemplate = this.translateService.instant('shell.nav.searchResultsEmptyDescription');
    this.searchResultsErrorPrefix = this.translateService.instant('shell.nav.searchResultsErrorPrefix');
    this.searchResultsContainersTitle = this.translateService.instant('shell.nav.searchResultsContainersTitle');
    this.searchResultsItemsTitle = this.translateService.instant('shell.nav.searchResultsItemsTitle');
    this.searchResultsContainerEmptyDescriptionLabel = this.translateService.instant('containers.page.noDescription');
    this.searchResultsItemEmptyDescriptionLabel = this.translateService.instant('containers.page.noDescription');
    this.searchResultsContainerMenuAriaLabelTemplate = this.translateService.instant(PRODUCT_CONTAINER_MENU_ARIA_LABEL_KEY);
    this.searchResultsItemMenuAriaLabelTemplate = this.translateService.instant(CONTAINER_ITEM_MENU_ARIA_LABEL_KEY);
    this.searchResultsRootLocationLabel = this.translateService.instant('containers.page.rootLocationLabel');
    if (this.searchResultsRootLocationLabel === 'containers.page.rootLocationLabel') {
      this.searchResultsRootLocationLabel = 'Root';
    }
    this.searchResultsContainerMenuItems = PRODUCT_CONTAINER_MENU_ITEMS;
    this.searchResultsItemMenuItems = CONTAINER_ITEM_MENU_ITEMS;
    this.statusBarAriaLabel = this.translateService.instant('shell.nav.statusBarAriaLabel');
    this.breadcrumbAriaLabel = this.translateService.instant('containers.page.breadcrumbAriaLabel');
    this.breadcrumbExpandLabel = this.translateService.instant('shell.breadcrumbs.showFullPath');
    this.breadcrumbFullPathLabel = this.translateService.instant('shell.breadcrumbs.fullPath');
    this.breadcrumbBackToPathLabel = this.translateService.instant('shell.breadcrumbs.backToPath');
    this.siblingSwitcherTitle = this.translateService.instant('containers.page.siblingSwitcher.title');
    this.siblingSwitcherRelocationTitle = this.translateService.instant(
      'containers.page.siblingSwitcher.relocationTitle',
    );
    this.siblingSwitcherLoadingLabel = this.translateService.instant('containers.page.siblingSwitcher.loading');
    this.siblingSwitcherEmptyLabel = this.translateService.instant('containers.page.siblingSwitcher.empty');
    this.siblingSwitcherErrorLabel = this.translateService.instant('containers.page.siblingSwitcher.error');
    this.siblingSwitcherRetryLabel = this.translateService.instant('containers.page.siblingSwitcher.retry');
    this.siblingSwitcherCurrentLabel = this.translateService.instant('containers.page.siblingSwitcher.current');
    this.searchModeItems = this.localizeSearchModes(this.searchModeDefinitions);
  }

  private loadSiblingContainers(): void {
    const breadcrumb = this.siblingSwitcherBreadcrumb;
    if (!breadcrumb || !isShellContainerBreadcrumb(breadcrumb)) {
      return;
    }

    const containerNavigation = breadcrumb.containerNavigation;

    const requestId = ++this.siblingSwitcherRequestId;
    this.siblingSwitcherLoading = true;
    this.siblingSwitcherError = '';
    this.siblingSwitcherItems = [];
    this.siblingSwitcherResults = [];
    const request = containerNavigation.parentContainerId
      ? this.containersService.getChildContainers(containerNavigation.parentContainerId)
      : this.containersService.getRootContainers();

    request
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          if (requestId !== this.siblingSwitcherRequestId || this.siblingSwitcherBreadcrumb?.id !== breadcrumb.id) {
            return;
          }

          this.siblingSwitcherItems = this.sortNamedContainers(items);
          this.siblingSwitcherResults = this.siblingSwitcherItems.map((container) => ({
            canEdit: container.canEdit,
            description: container.description,
            hasAssignedQrCode: false,
            id: container.id,
            imageUrl: container.imageUrl,
            isFavorite: container.isFavorite,
            isCurrent: container.id === this.siblingSwitcherCurrentContainerId,
            isSharedRoot: container.isSharedRoot,
            name: container.name,
            path: [
              ...breadcrumb.containerNavigation.parentPath,
              {id: container.id, name: container.name},
            ],
          }));
          this.siblingSwitcherLoading = false;
        },
        error: () => {
          if (requestId !== this.siblingSwitcherRequestId || this.siblingSwitcherBreadcrumb?.id !== breadcrumb.id) {
            return;
          }

          this.siblingSwitcherLoading = false;
          this.siblingSwitcherError = this.siblingSwitcherErrorLabel;
        },
      });
  }

  private positionSiblingSwitcher(anchorX: number, anchorY: number): void {
    const viewportWidth = typeof window === 'undefined' ? 480 : window.innerWidth;
    const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
    const margin = 8;
    const panelWidth = Math.min(480, viewportWidth - (margin * 2));

    this.siblingSwitcherPanelWidth = panelWidth;
    this.siblingSwitcherPanelLeft = Math.min(Math.max(anchorX, margin), viewportWidth - panelWidth - margin);
    this.siblingSwitcherPanelTop = Math.min(Math.max(anchorY + 8, margin), viewportHeight - 160);
  }

  private sortNamedContainers<T extends {id: string; name: string}>(items: readonly T[]): T[] {
    const locale = this.translateService.currentLang || this.appLanguageService.languageCode || undefined;
    return sortByNaturalName(items, locale);
  }

  private rebuildNavItems(): void {
    this.navItems = this.filterNavItemsByAccess(this.localizeNavItems(this.navItemDefinitions));
    const guides = buildGuidesNavigation(
      this.appLanguageService.languageCode, environment.webAppBaseUrl, this.isNativePlatform,
      {
        guides: this.translateService.instant('shell.nav.guides.title'),
        external: this.translateService.instant(this.isNativePlatform
          ? 'shell.nav.guides.externalBrowser' : 'shell.nav.guides.newTab'),
      },
    );
    this.navItems.splice(this.navItems.findIndex(item => item.id === 'feedback') + 1, 0, guides);
    this.applyReportBadgeCounts(this.navItems);
    this.applyJournalBadgeCount(this.navItems);
    this.applyBasketBadgeCount(this.navItems);
    this.applyModerationBadgeCount(this.navItems);
    const items = this.navItems.reduce<SmNavMenuItemModel[]>((all, item) => all.concat(item.children ?? [item]), []);
    this.quickNavItems = ['inventory-containers', 'basket-needs', 'inventory-favorites', 'inventory-transfers', 'reports-latest']
      .map(id => items.find(candidate => candidate.id === id && !candidate.disabled))
      .filter((item): item is SmNavMenuItemModel => !!item)
      .map(item => ({...item,
        iconName: item.id === 'basket-needs' ? this.navItems.find(parent => parent.id === 'basket')?.iconName ?? item.iconName : item.iconName,
        text: item.id === 'basket-needs' ? this.translateService.instant('basket.title') : item.text,
      }));
  }

  private async openGuide(href: string): Promise<void> {
    if (this.openingGuide) {
      return;
    }
    this.openingGuide = true;
    try {
      if (!await this.externalBrowser.open(href)) {
        await this.confirmationDialogService.open({
          title: this.translateService.instant('shell.nav.guides.errorTitle'),
          description: this.translateService.instant('shell.nav.guides.errorDescription'),
          dialogType: 'warning', showCancelButton: false,
          cancelLabel: this.translateService.instant('common.actions.cancel'),
          confirmLabel: this.translateService.instant('shell.nav.guides.close'),
          closeLabel: this.translateService.instant('shell.nav.guides.close'),
        });
      }
    } finally {
      this.openingGuide = false;
    }
  }

  private filterNavItemsByAccess(items: SmNavMenuItemModel[]): SmNavMenuItemModel[] {
    const allowedItems: SmNavMenuItemModel[] = [];

    for (const item of items) {
      if (item.id === 'administration' && !this.isAdministrator) {
        continue;
      }

      if (item.id === 'moderation' && !this.canModerate) {
        continue;
      }

      const children = item.children?.length ? this.filterNavItemsByAccess(item.children) : undefined;
      allowedItems.push({
        ...item,
        children: children?.length ? children : undefined,
      });
    }

    return allowedItems;
  }

  private updateSearchResultsLayout(): void {
    if (!this.searchResultsOpen || !this.searchStack) {
      return;
    }

    const stackRect = this.searchStack.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const gutter = 6;
    const compactViewport = viewportWidth <= APP_SHELL_COMPACT_MAX_WIDTH_PX;

    this.searchResultsTop = Math.ceil(stackRect.height);
    this.searchResultsViewportTop = Math.ceil(stackRect.bottom);

    if (compactViewport) {
      this.searchResultsLeft = gutter;
      this.searchResultsWidth = Math.max(0, viewportWidth - gutter * 2);
      return;
    }

    const viewportSafeWidth = Math.max(0, viewportWidth - gutter * 2);
    const desiredWidth = Math.min(viewportSafeWidth, Math.max(360, Math.min(stackRect.width * 2, 640)));
    const viewportLeft = Math.min(
      Math.max(gutter, stackRect.left),
      Math.max(gutter, viewportWidth - gutter - desiredWidth),
    );

    this.searchResultsLeft = viewportLeft - stackRect.left;
    this.searchResultsWidth = Math.min(desiredWidth, Math.max(0, viewportWidth - gutter - viewportLeft));
  }

  private localizeNavItems(definitions: ShellNavItemDefinition[]): SmNavMenuItemModel[] {
    return definitions.map((definition) => ({
      id: definition.id,
      text: this.translateService.instant(definition.textKey),
      iconName: definition.iconName,
      badgeText: definition.badgeText,
      route: definition.route,
      routeHref: typeof definition.route === 'string' ? this.buildRoutePath(definition.route) : undefined,
      disabled: definition.disabled,
      selected: definition.selected,
      tone: definition.tone,
      children: definition.children ? this.localizeNavItems(definition.children) : undefined,
    }));
  }

  private applyReportBadgeCounts(items: SmNavMenuItemModel[]): void {
    const reportsItem = items.find((item) => item.id === 'reports');
    if (!reportsItem?.children?.length) {
      return;
    }

    reportsItem.children = reportsItem.children.map((child) => {
      if (child.id === 'reports-expiring') {
        return {
          ...child,
          badgeText: this.reportsExpiringCountLabel,
        };
      }

      if (child.id === 'reports-expired') {
        return {
          ...child,
          badgeText: this.reportsExpiredCountLabel,
        };
      }

      return child;
    });
  }

  private applyJournalBadgeCount(items: SmNavMenuItemModel[]): void {
    const journal = items.find(item => item.id === 'reports')?.children?.find(item => item.id === 'reports-latest');
    if (!journal || this.reportsCountLatest === null) return;
    journal.badgeText = String(this.reportsCountLatest);
    journal.badgeDescription = this.translateService.instant('eventJournal.last24HoursCount', {count: this.reportsCountLatest});
  }

  private get reportsExpiringCountLabel(): string | undefined {
    return String(this.reportsCountExpiring);
  }

  private get reportsExpiredCountLabel(): string | undefined {
    return String(this.reportsCountExpired);
  }

  private applyModerationBadgeCount(items: SmNavMenuItemModel[]): void {
    const moderationItem = items.find((item) => item.id === 'moderation');
    if (moderationItem) {
      moderationItem.badgeText = String(this.moderationPendingCount);
    }
  }

  private applyBasketBadgeCount(items: SmNavMenuItemModel[]): void {
    const basketNeedsItem = items.find(item => item.id === 'basket')?.children?.find(item => item.id === 'basket-needs');
    if (basketNeedsItem) {
      basketNeedsItem.badgeText = this.basketOutstandingCount && this.basketOutstandingCount > 0
        ? String(this.basketOutstandingCount)
        : undefined;
    }
  }

  private reportsCountExpiring = 0;
  private reportsCountExpired = 0;
  private moderationPendingCount = 0;
  private basketOutstandingCount: number | null = null;

  private localizeSearchModes(definitions: ShellSearchModeDefinition[]): SmContextMenuItemModel[] {
    return definitions.map((definition) => ({
      id: definition.id,
      text: this.translateService.instant(definition.textKey),
      iconName: definition.iconName,
      disabled: definition.disabled,
      selected: definition.id === this.selectedSearchModeId,
      checked: definition.id === this.selectedSearchModeId,
    }));
  }

  private resolveSelectedNavId(url: string): string {
    const pathname = stripLocalizedRouteLanguageFromPathname(url);
    const paths = [
      ['/administration/releases', 'administration-releases'],
      ['/administration/users', 'administration-users'],
      ['/administration/notifications', 'administration-notifications'],
      ['/administration/appearance', 'administration-appearance'],
      ['/administration/operational-settings', 'administration-operational-settings'],
      ['/administration/audits', 'administration-audits'],
      ['/administration/payment-providers', 'administration-payment-providers'],
      ['/administration/pricing-plans', 'administration-pricing-plans'],
      ['/moderation', 'moderation'],
      ['/administration/feedback', 'administration-feedback'],
      ['/feedback', 'feedback'],
      ['/basket/shops', 'basket-shops'],
      ['/basket', 'basket-needs'],
      ['/inventory/containers', 'inventory-containers'],
      ['/qr-labels/packages', 'qr-codes-packages'],
      ['/qr-labels', 'qr-codes-profiles'],
      ['/people/received', 'people-received-invites'],
      ['/people/sent', 'people-sent-invites'],
      ['/people/trusted-contacts', 'people-trusted-contacts'],
      ['/profile/settings', 'profile-settings'],
      ['/profile/mailings', 'profile-mailings'],
      ['/profile/shops', 'profile-shops'],
      ['/profile/subscription', 'profile-subscription'],
      ['/people', 'people-received-invites'],
      ['/profile/onboarding', 'profile-onboarding'],
      ['/about', 'about'],
      ['/reports/expiring', 'reports-expiring'],
      ['/reports/expired', 'reports-expired'],
      ['/reports/last-changes', 'reports-latest'],
      ['/write-off', 'inventory-writeoffs'],
      ['/relocation', 'inventory-transfers'],
      ['/home', 'home'],
    ];
    return paths.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? 'home';
  }

  private syncBreadcrumbsForCurrentRoute(url: string): void {
    const pathname = stripLocalizedRouteLanguageFromPathname(url);

    if (pathname.startsWith('/home')) {
      this.shellBreadcrumbsService.setBreadcrumbs([
        {
          id: 'home',
          label: this.translateService.instant('shell.nav.items.home'),
          route: '/home',
        },
      ]);
      return;
    }

    if (pathname.startsWith('/feedback') || pathname.startsWith('/administration/users') || pathname.startsWith('/administration/feedback') || pathname.startsWith('/administration/notifications') || pathname.startsWith('/administration/operational-settings') || pathname.startsWith('/about')) {
      this.shellBreadcrumbsService.clear();
    }
  }

  get websiteRootHref(): string {
    return this.buildRoutePath('/');
  }

  get websiteExternalHref(): string {
    return new URL(this.websiteRootHref, new URL(environment.webAppBaseUrl).origin).toString();
  }

  private updateRouteUiState(url: string): void {
    const pathname = stripLocalizedRouteLanguageFromPathname(url.split(/[?#]/)[0]);
    this.resetBreadcrumbPresentation();
    this.selectedNavId = this.resolveSelectedNavId(url);
    this.isAboutRoute = pathname.startsWith('/about');
    this.syncBreadcrumbsForCurrentRoute(url);
    this.applyLaunchQrQuery(url);
  }

  private resetBreadcrumbPresentation(): void {
    this.breadcrumbsCollapsed = this.isCompactViewport();
  }

  private applyLaunchQrQuery(url: string): void {
    const qrToken = this.readLaunchQrToken(url);
    if (!qrToken) {
      this.qrLaunchToken = null;
      this.cancelQrLaunchRetryTimeout();
      return;
    }

    this.qrLaunchToken = qrToken;
    this.handleSearchQueryChange(qrToken);
    this.scheduleQrLaunchRetry(qrToken);
  }

  private async configureQrLaunchRecovery(): Promise<void> {
    this.appStateListener = await App.addListener('appStateChange', ({isActive}) => {
      if (!isActive) {
        return;
      }

      this.ngZone.run(() => {
        this.resetBreadcrumbPresentation();
        this.shellBreadcrumbsComponent?.refreshFullPathPosition();
        if (this.currentUser) {
          this.store.dispatch(ReportsActions.applicationResumed());
          this.basketService.refreshNavigationCount();
        }
        this.retryQrLaunchSearchIfStalled();
      });
    });
  }

  private scheduleQrLaunchRetry(qrToken: string): void {
    this.cancelQrLaunchRetryTimeout();
    this.qrLaunchRetryTimeoutId = window.setTimeout(() => {
      if (this.qrLaunchToken !== qrToken) {
        return;
      }

      this.retryQrLaunchSearchIfStalled();
    }, 2500);
  }

  private retryQrLaunchSearchIfStalled(): void {
    if (!this.qrLaunchToken) {
      return;
    }

    if (this.searchQuery.trim() !== this.qrLaunchToken) {
      return;
    }

    if (!this.searchResultsLoading) {
      return;
    }

    this.handleSearchQueryChange(this.qrLaunchToken);
  }

  private cancelQrLaunchRetryTimeout(): void {
    if (!this.qrLaunchRetryTimeoutId) {
      return;
    }

    clearTimeout(this.qrLaunchRetryTimeoutId);
    this.qrLaunchRetryTimeoutId = null;
  }

  private buildContainerBreadcrumbTrail(route: string): ShellBreadcrumb[] | null {
    if (!route.startsWith('/inventory/containers')) {
      return null;
    }

    const targetIndex = this.breadcrumbs.findIndex((breadcrumb) => breadcrumb.route === route);
    if (targetIndex < 0) {
      return null;
    }

    return this.breadcrumbs.slice(0, targetIndex + 1);
  }

  private readLaunchQrToken(url: string): string {
    if (typeof window === 'undefined') {
      return '';
    }

    try {
      return new URL(url, window.location.origin).searchParams.get('qr')?.trim() ?? '';
    } catch {
      return '';
    }
  }

  private scheduleSearch(): void {
    this.searchScheduler.invalidate();
    this.cancelSearchTimeout();

    const query = this.searchQuery.trim();
    if (query.length < 2) {
      this.searchResults = {
        containers: [],
        hasMoreContainers: false,
        hasMoreItems: false,
        items: [],
      };
      this.searchResultsLoading = false;
      this.searchResultsError = '';
      return;
    }

    this.searchResultsLoading = true;
    this.searchResultsError = '';
    this.searchScheduler.schedule(() => {
      void this.performSearch();
    });
  }

  private cancelSearchTimeout(): void {
    this.searchScheduler.cancelTimer();
  }

  searchTagSelection: TagSelection = {authorIds: [], allAuthors: false};
  selectSearchTagAuthors(value: TagSelection): void { this.searchTagSelection = value; void this.performSearch(); }
  private async performSearch(): Promise<void> {
    const query = this.searchQuery.trim();
    const requestId = this.searchScheduler.invalidate();

    if (query.length < 2) {
      this.searchResults = {
        containers: [],
        hasMoreContainers: false,
        hasMoreItems: false,
        items: [],
      };
      this.searchResultsLoading = false;
      return;
    }

    this.searchResultsLoading = true;

    try {
      const results = await firstValueFrom(this.globalSearchService.search(query, this.selectedSearchModeId, this.searchScan?.type ?? 'text', this.searchTagSelection));
      if (!this.searchScheduler.isCurrent(requestId)) {
        return;
      }

      this.searchResults = results;
    } catch {
      if (!this.searchScheduler.isCurrent(requestId)) {
        return;
      }

      this.searchResults = {
        containers: [],
        hasMoreContainers: false,
        hasMoreItems: false,
        items: [],
      };
      this.searchResultsError = this.translateService.instant('shell.nav.searchResultsGenericError');
    } finally {
      if (this.searchScheduler.isCurrent(requestId)) {
        this.searchResultsLoading = false;
      }
    }
  }

  private buildRoutePath(route: string): string {
    return buildPlatformAwareRoutePathname(route, this.appLanguageService.languageCode);
  }

  private reloadCurrentPage(): void {
    if (typeof window !== 'undefined' && typeof window.location.reload === 'function') {
      window.location.reload();
      return;
    }

    void this.router.navigateByUrl(this.router.url, {replaceUrl: true});
  }

  private resetAccountSearch(): void {
    this.handleSearchResultsClose();
    this.searchTagSelection = {authorIds: [], allAuthors: false};
    this.searchQuery = ''; this.searchScan = null;
    this.searchResults = {containers: [], items: [], hasMoreContainers: false, hasMoreItems: false};
  }

  private async syncProfileForSession(session: AuthSession | null): Promise<void> {
    if (this.profileSession?.user.id !== session?.user.id) this.resetAccountSearch();
    this.profileSession = session;
    this.profileRecoveryPending = false;
    if (!session) {
      this.profileRequestId += 1;
      this.currentUser = null;
      this.isAdministrator = false;
      this.canModerate = false;
      this.publicationModerationService.resetPendingCount();
      this.navProfile = {
        email: '',
        roleLabel: '',
        avatarText: 'U',
      };
      this.refreshLocalizedContent();
      return;
    }

    this.currentUser = null;
    this.navProfile = this.buildNavProfile(null, session.user.email ?? session.user.username);
    const requestId = ++this.profileRequestId;

    try {
      const currentUser = await firstValueFrom(this.authService.getCurrentUser());
      if (requestId !== this.profileRequestId) {
        return;
      }

      this.currentUser = currentUser;
      this.store.dispatch(ReportsActions.effectiveTimeZoneChanged({
        timeZoneId: currentUser.preferredTimeZoneId || readDeviceTimeZoneId(),
      }));
      this.isAdministrator = currentUser.roleCode === 'Administrator';
      this.canModerate = currentUser.roleCode === 'Administrator' || currentUser.roleCode === 'Moderator';
      if (this.canModerate) {
        this.publicationModerationService.refreshPendingCount()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({error: () => undefined});
      }
      this.navProfile = this.buildNavProfile(currentUser, currentUser.email);
      this.refreshLocalizedContent();
    } catch {
      if (requestId !== this.profileRequestId) {
        return;
      }

      this.currentUser = null;
      this.isAdministrator = false;
      this.canModerate = false;
      this.profileRecoveryPending = true;
      this.refreshLocalizedContent();
    }
  }

  private buildNavProfile(currentUser: CurrentUserResponse | null, fallbackEmail = ''): SmNavBarProfileModel {
    if (!currentUser) {
      return {
        email: fallbackEmail,
        roleLabel: '',
        avatarText: this.buildAvatarText(fallbackEmail),
      };
    }

    return {
      email: currentUser.email,
      roleLabel: this.translateService.instant('shell.nav.roleLabel', {
        role: this.translateService.instant(this.getRoleTranslationKey(currentUser.roleCode)),
      }),
      avatarText: this.buildAvatarText(currentUser.firstName || currentUser.email || currentUser.username),
    };
  }

  private buildAvatarText(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return 'U';
    }

    return trimmed[0].toUpperCase();
  }

  private getRoleTranslationKey(roleCode: CurrentUserResponse['roleCode']): string {
    switch (roleCode) {
      case 'Administrator':
        return 'shell.nav.roles.administrator';
      case 'NewsMaker':
        return 'shell.nav.roles.newsMaker';
      case 'Moderator':
        return 'shell.nav.roles.moderator';
      case 'User':
      default:
        return 'shell.nav.roles.user';
    }
  }

  private async closeMainMenuIfMobileOverlay(): Promise<void> {
    if (!window.matchMedia('(width < 992px)').matches) {
      return;
    }

    await this.menuController.close('main-menu');
  }

}
